import { AuthService, type AuthUser } from './AuthService.js';

export const MOCK_ADMIN: AuthUser = {
  uid: 'mock-admin-001',
  email: 'admin@k-ucap.local',
  displayName: 'Mock Admin',
  photoURL: null,
  authProvider: 'mock',
};

export const MOCK_VIEWER: AuthUser = {
  uid: 'mock-viewer-001',
  email: 'viewer@k-ucap.local',
  displayName: 'Mock Viewer',
  photoURL: null,
  authProvider: 'mock',
};

const MOCK_USER_KEY = 'k-ucap-mock-user';

export class MockAuthService implements AuthService {
  private currentUser: AuthUser | null = null;
  private handlers: Set<(user: AuthUser | null) => void> = new Set();

  async initialize(): Promise<void> {
    try {
      const stored = localStorage.getItem(MOCK_USER_KEY);
      if (stored) {
        this.currentUser = JSON.parse(stored) as AuthUser;
      }
    } catch {
      // ignore storage errors
    }
  }

  async signInWithEmail(email: string, _password: string): Promise<AuthUser> {
    if (email.includes('admin')) {
      this.currentUser = MOCK_ADMIN;
    } else if (email.includes('viewer')) {
      this.currentUser = MOCK_VIEWER;
    } else {
      this.currentUser = {
        uid: 'mock-user-001',
        email,
        displayName: 'Mock User',
        photoURL: null,
        authProvider: 'mock',
      };
    }
    this.notify();
    return this.currentUser;
  }

  async signInWithGoogle(): Promise<AuthUser> {
    this.currentUser = MOCK_ADMIN;
    this.notify();
    return this.currentUser;
  }

  async signInAsMock(role: 'ADMIN' | 'VIEWER'): Promise<AuthUser> {
    this.currentUser = role === 'ADMIN' ? MOCK_ADMIN : MOCK_VIEWER;
    this.persist();
    this.notify();
    return this.currentUser;
  }

  async signOut(): Promise<void> {
    this.currentUser = null;
    this.remove();
    this.notify();
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  subscribeAuthState(handler: (user: AuthUser | null) => void): () => void {
    this.handlers.add(handler);
    handler(this.currentUser);
    return () => this.handlers.delete(handler);
  }

  async sendPasswordResetEmail(_email: string): Promise<void> {
    // no-op in mock
  }

  async getIdToken(): Promise<string | null> {
    return this.currentUser ? 'mock-token' : null;
  }

  async refreshCurrentUser(): Promise<AuthUser | null> {
    return this.currentUser;
  }

  private notify(): void {
    this.handlers.forEach((h) => h(this.currentUser));
  }

  private persist(): void {
    try {
      if (this.currentUser) {
        localStorage.setItem(MOCK_USER_KEY, JSON.stringify(this.currentUser));
      } else {
        localStorage.removeItem(MOCK_USER_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }

  private remove(): void {
    try {
      localStorage.removeItem(MOCK_USER_KEY);
    } catch {
      // ignore storage errors
    }
  }
}

export const createMockAuthService = (): MockAuthService => new MockAuthService();
